import React, { useState, useEffect } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader2, IndianRupee } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    FormDescription,
} from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { numberToIndianWords } from "@/utils/numberToWords";
import { stringMandatory, pincodeNumberString, panString } from "@/helpers/validations";
import JourneyHeader from "@/components/JourneyHeader";
import CallbackHelp from "@/components/CallbackHelp";

const MAX_AMOUNT = 10000000;

const formSchema = z.object({
    individual_name: stringMandatory("Name"),
    amount: z.string().refine((val) => {
        const num = Number(val);
        return !isNaN(num) && num > 0 && num <= MAX_AMOUNT;
    }, {
        message: `Amount must be greater than 0 and up to ${MAX_AMOUNT}`,
    }),
    business_pincode: pincodeNumberString("Business Pincode"),
    individual_pan_number: panString("PAN"),
    residence_type: stringMandatory("Residence Type"),
});

const LoanAmountWhitespace = () => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const [amountInWords, setAmountInWords] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isCallbackOpen, setIsCallbackOpen] = useState(false);
    const [prefillAmount, setPrefillAmount] = useState<string>("");

    type FormValues = z.infer<typeof formSchema>;
    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        mode: "onTouched",
        defaultValues: {
            individual_name: "",
            amount: "",
            business_pincode: "",
            individual_pan_number: "",
            residence_type: "",
        },
    });

    // Fetch pre-approved loan amount from backend and pre-fill the amount field
    useEffect(() => {
        const fetchPreApprovedAmount = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/get_pre_approved_loan_amount/`);
                if (response.status === 200 && response.data?.loan_amount) {
                    const amt = String(response.data.loan_amount);
                    setPrefillAmount(amt);
                    form.setValue("amount", amt, { shouldValidate: true, shouldDirty: true });
                }
            } catch (error) {
                // Non-critical: silently ignore if amount cannot be pre-filled
                console.warn("Could not fetch pre-approved loan amount:", error);
            }
        };
        fetchPreApprovedAmount();
    }, []);

    const amountValue = form.watch("amount");

    useEffect(() => {
        const num = Number(amountValue);
        if (!isNaN(num) && num > 0) {
            if (num <= MAX_AMOUNT) {
                setAmountInWords(numberToIndianWords(num));
            } else {
                setAmountInWords("Amount exceeds limit");
            }
        } else {
            setAmountInWords("");
        }
    }, [amountValue]);

    const onSubmit = async (values: FormValues) => {
        setIsLoading(true);
        try {
            console.log("Submitting whitespace loan details:", values);

            const response = await axios.post(`${API_BASE_URL}/api/set_whitespace_loan_details/`, {
                name: values.individual_name,
                loanAmount: values.amount,
                business_pincode: values.business_pincode,
                pan_number: values.individual_pan_number,
                residence_type: values.residence_type,
            });

            if (response.status === 200) {
                const nextPage = response.data?.next_page_url;
                if (nextPage) {
                    navigate(nextPage);
                } else {
                    navigate("/journey-error");
                }
            } else {
                throw new Error("Failed to save details");
            }
        } catch (error) {
            console.error("Error submitting details:", error);
            toast({
                variant: "destructive",
                title: "Error",
                description: "Something went wrong. Please try again.",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
        <div className="min-h-screen bg-intellend-white pb-10">
            <JourneyHeader />

            <div className="container mx-auto px-4 py-8 flex justify-center items-center min-h-[calc(100vh-100px)]">
                <Card className="w-full max-w-lg shadow-lg">
                    <CardHeader className="text-center">
                        <CardTitle className="text-2xl font-bold text-intellend-green">Loan Requirement</CardTitle>
                        <CardDescription className="text-base mt-2">
                            Loans upto ₹1 Crore available from multiple lenders, subject to verification.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">

                                <FormField
                                    control={form.control}
                                    name="individual_name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm">Your Name (as per PAN card) <span className="text-red-500">*</span></FormLabel>
                                            <FormControl>
                                                <Input placeholder="Full name" {...field} className="h-9" showSuccess={!!field.value && !form.formState.errors.individual_name} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="amount"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm">Loan Amount Required <span className="text-red-500">*</span></FormLabel>
                                            <FormControl>
                                                <div className="relative">
                                                    <Input
                                                        placeholder="Requested Loan Amount"
                                                        type="number"
                                                        className="pr-16 text-lg py-6"
                                                        {...field}
                                                        max={MAX_AMOUNT}
                                                        showSuccess={!!field.value && Number(field.value) > 0 && Number(field.value) <= MAX_AMOUNT}
                                                    />
                                                    <div className="absolute inset-y-0 right-10 flex items-center pointer-events-none text-gray-500">
                                                        <IndianRupee className="h-5 w-5" />
                                                    </div>
                                                </div>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                {prefillAmount && (
                                                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
                                                        ℹ️ Loan amount pre-filled from your earlier entry — <strong>you may update it below.</strong>
                                                    </div>
                                                )}
                
                                                {amountInWords && (
                                                    <div className="bg-gray-50 p-4 rounded-md border border-gray-200 text-center">
                                                        <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">In Words</p>
                                                        <p className={`text-lg font-semibold mt-1 ${amountInWords === "Amount exceeds limit" ? "text-red-500" : "text-intellend-green"}`}>
                                                            {amountInWords} {amountInWords !== "Amount exceeds limit" && "Rupees Only"}
                                                        </p>
                                                    </div>
                                                )}


                                {Number(amountValue) > 0 && Number(amountValue) <= 500000 && (
                                    <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-800">
                                        ✅ Less than ₹5 Lakhs — <strong>Fast processing with minimal documentation.</strong>
                                    </div>
                                )}
                                {Number(amountValue) > 500000 && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                                        📋 More than ₹5 Lakhs — <strong>Requires additional documentation.</strong>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField
                                        control={form.control}
                                        name="business_pincode"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-sm">Business Pincode <span className="text-red-500">*</span></FormLabel>
                                                <FormControl>
                                                    <Input placeholder="6-digit Pincode" maxLength={6} {...field} className="h-9" inputMode="numeric" showSuccess={!!field.value && !form.formState.errors.business_pincode} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="individual_pan_number"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-sm">Individual PAN No<span className="text-red-500">*</span></FormLabel>
                                                <FormControl>
                                                    <Input placeholder="10-digit PAN No" maxLength={10} {...field} className="uppercase h-9" showSuccess={!!field.value && !form.formState.errors.individual_pan_number} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <FormField
                                    control={form.control}
                                    name="residence_type"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm">Residence is owned or rented<span className="text-red-500">*</span></FormLabel>
                                            <Select onValueChange={field.onChange} value={field.value || ""}>
                                                <FormControl>
                                                    <SelectTrigger className="h-9" showSuccess={!!field.value && !form.formState.errors.residence_type}>
                                                        <SelectValue placeholder="Select" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="self_owned">Self Owned</SelectItem>
                                                    <SelectItem value="rented">Rented</SelectItem>
                                                    <SelectItem value="owned_parental">Owned (Parental)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <Button
                                    type="submit"
                                    className="w-full text-lg py-6 bg-intellend-green hover:bg-intellend-green/90"
                                    disabled={isLoading || !form.formState.isValid}
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                            Processing...
                                        </>
                                    ) : (
                                        "Proceed"
                                    )}
                                </Button>

                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => setIsCallbackOpen(true)}
                                >
                                    Request Callback
                                </Button>
                            </form>
                        </Form>
                    </CardContent>
                </Card>
            </div>
        </div>

        <CallbackHelp
            isOpen={isCallbackOpen}
            setIsOpen={setIsCallbackOpen}
            showOk={false}
            showCallUs={true}
            showWhatsappUs={true}
            silentlyTrigger={true}
            title="Intellend Support"
            description="Reach out to us using one of the following options"
        />
        </>
    );
};

export default LoanAmountWhitespace;
